import React, { useState, useRef } from 'react';
import type { WidgetItem, WidgetProperties, WidgetType } from './types';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Switch,
  FormControlLabel,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  ThemeProvider,
  createTheme,
  CircularProgress
} from '@mui/material';
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  Plus,
  Trash2,
  Sliders,
  MousePointerClick,
  Upload,
  X
} from 'lucide-react';
import baseapi from '../../services/baseapi';
import { showSuccess, showError } from '../../utils/toast';

import { useTheme } from '../../context/ThemeContext';

const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

const getPropsTheme = (isDark: boolean) =>
  createTheme({
    palette: {
      mode: isDark ? 'dark' : 'light',
      primary: { main: '#f97316' },
      background: { default: isDark ? '#0f172a' : '#f8fafc', paper: isDark ? '#1e293b' : '#ffffff' },
      text: { primary: isDark ? '#f8fafc' : '#0f172a', secondary: isDark ? '#94a3b8' : '#64748b' }
    },
    typography: {
      fontFamily: '"Inter", "Segoe UI", Roboto, sans-serif',
      fontSize: 12
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 6,
              fontSize: 12,
              backgroundColor: isDark ? '#1e293b' : '#f8fafc',
              '& fieldset': { borderColor: isDark ? '#334155' : '#cbd5e1' },
              '&:hover fieldset': { borderColor: '#f97316' },
              '&.Mui-focused fieldset': { borderColor: '#f97316' }
            }
          }
        }
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: 12,
            color: isDark ? '#9ca3af' : '#64748b',
            '&.Mui-focused': { color: '#f97316' }
          }
        }
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            padding: '6px 10px',
            fontSize: 12
          }
        }
      },
      MuiMenuItem: {
        styleOverrides: {
          root: { fontSize: 12 }
        }
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            fontSize: 11,
            padding: '4px 8px',
            color: isDark ? '#94a3b8' : '#64748b',
            borderColor: isDark ? '#334155' : '#cbd5e1',
            '&.Mui-selected': {
              backgroundColor: 'rgba(249, 115, 22, 0.2)',
              color: '#f97316',
              borderColor: '#f97316'
            }
          }
        }
      }
    }
  });

const FONT_FAMILY_OPTIONS = [
  { label: 'Inter (Clean Sans)', value: 'Inter, sans-serif' },
  { label: 'Roboto', value: 'Roboto, sans-serif' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Georgia (Serif)', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
  { label: 'Courier New (Mono)', value: '"Courier New", monospace' },
  { label: 'Monaco (Mono)', value: 'Monaco, monospace' }
];

export const COLOR_PALETTES: { label: string; value: string; colors: string[] }[] = [
  { label: 'Classic Corporate', value: 'classic', colors: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'] },
  { label: 'Indigo Tech', value: 'indigo', colors: ['#6366f1', '#38bdf8', '#a855f7', '#ec4899', '#14b8a6', '#f59e0b'] },
  { label: 'Emerald Health', value: 'emerald', colors: ['#10b981', '#06b6d4', '#84cc16', '#3b82f6', '#14b8a6', '#eab308'] },
  { label: 'Amber Warmth', value: 'amber', colors: ['#f59e0b', '#f97316', '#ef4444', '#eab308', '#84cc16', '#06b6d4'] },
  { label: 'Rose Energy', value: 'rose', colors: ['#f43f5e', '#ec4899', '#8b5cf6', '#3b82f6', '#06b6d4', '#10b981'] },
  { label: 'Ocean Depth', value: 'ocean', colors: ['#0284c7', '#06b6d4', '#0d9488', '#3b82f6', '#6366f1', '#8b5cf6'] },
  { label: 'Sunset Glow', value: 'sunset', colors: ['#f97316', '#fbbf24', '#f43f5e', '#8b5cf6', '#06b6d4', '#10b981'] },
  { label: 'Cyber Neon', value: 'cyber', colors: ['#06b6d4', '#a855f7', '#ec4899', '#3b82f6', '#10b981', '#f59e0b'] }
];

export const PRESET_COLORS = [
  { label: 'Dark Slate', value: '#0f172a' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Blue', value: '#3b82f6' },
  { label: 'Emerald', value: '#10b981' },
  { label: 'Purple', value: '#8b5cf6' },
  { label: 'Rose', value: '#f43f5e' },
  { label: 'Amber', value: '#f59e0b' },
  { label: 'Cyan', value: '#06b6d4' },
  { label: 'Slate', value: '#64748b' },
  { label: 'White', value: '#ffffff' }
];

export interface RightPropsPanelProps {
  selectedWidget?: WidgetItem | null;
  onUpdateWidget: (id: string, properties: Partial<WidgetProperties>) => void;
  onDeleteWidget: (id: string) => void;
  onAddWidget?: (type: WidgetType) => void;
  onSyncHeaderToAll?: () => void;
  onSyncFooterToAll?: () => void;
  onOpenChartModal?: () => void;
  activeReportId?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

const RightPropsPanel: React.FC<RightPropsPanelProps> = ({
  selectedWidget,
  onUpdateWidget,
  onDeleteWidget,
  onAddWidget,
  onOpenChartModal,
  activeReportId,
  isOpen = true,
  onClose
}) => {
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, widgetId: string) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      showError(`Image is too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum allowed size is 5MB.`);
      e.target.value = '';
      return;
    }

    const previewUrl = URL.createObjectURL(file);

    try {
      setIsUploadingImage(true);
      const formData = new FormData();
      formData.append('file', file);

      let uploadedData: any = null;
      if (activeReportId) {
        const res = await baseapi.post(`custom_reports/${activeReportId}/upload-image`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedData = res.data?.data || res.data;
      }

      const imageName = uploadedData?.image_name || file.name;
      const originalName = uploadedData?.original_name || file.name;
      const imageKey = uploadedData?.image_key || '';
      const finalUrl = uploadedData?.url || uploadedData?.image_url || (imageKey ? `/api/${imageKey}` : previewUrl);

      onUpdateWidget(widgetId, {
        image_name: imageName,
        original_name: originalName,
        image_url: finalUrl,
        imageFit: selectedWidget?.properties?.imageFit || 'contain'
      });

      showSuccess(`Uploaded image: ${originalName}`);
    } catch (err: any) {
      showError(err.response?.data?.message || err.message || 'Image upload error, applied local preview');
      onUpdateWidget(widgetId, {
        image_name: file.name,
        original_name: file.name,
        image_url: previewUrl,
        imageFit: selectedWidget?.properties?.imageFit || 'contain'
      });
    } finally {
      setIsUploadingImage(false);
      e.target.value = '';
    }
  };

  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const propsTheme = React.useMemo(() => getPropsTheme(isDark), [isDark]);

  if (!isOpen) return null;

  return (
    <ThemeProvider theme={propsTheme}>
      <Box
        sx={{
          width: 320,
          height: '100%',
          bgcolor: isDark ? '#0f172a' : '#ffffff',
          borderLeft: isDark ? '1px solid #1f2937' : '1px solid #e2e8f0',
          display: 'flex',
          flexDirection: 'column',
          flexShrink: 0,
          zIndex: 25,
          userSelect: 'none',
          boxShadow: isDark ? '-4px 0 24px rgba(0, 0, 0, 0.25)' : '-4px 0 24px rgba(0, 0, 0, 0.05)'
        }}
      >
        {/* Top Header Bar */}
        <Box
          sx={{
            height: 48,
            px: 1.75,
            bgcolor: isDark ? '#0f172a' : '#f8fafc',
            borderBottom: isDark ? '1px solid #1f2937' : '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Sliders size={16} color="#f97316" />
            <Typography sx={{ color: isDark ? 'white' : '#0f172a', fontWeight: 700, fontSize: 13, textTransform: 'capitalize' }}>
              {selectedWidget ? `${selectedWidget.type} Properties` : 'Element Properties'}
            </Typography>
          </Box>

          {onClose && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: isDark ? '#94a3b8' : '#64748b', p: 0.5, '&:hover': { color: isDark ? '#ffffff' : '#0f172a', bgcolor: isDark ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)' } }}
            >
              <X size={16} />
            </IconButton>
          )}
        </Box>

        {/* Content Body */}
        <Box sx={{ p: 1.75, display: 'flex', flexDirection: 'column', gap: 1.75, overflowY: 'auto', flex: 1 }}>
          {!selectedWidget ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1.5, textAlign: 'center', color: isDark ? '#64748b' : '#94a3b8' }}>
              <MousePointerClick size={36} color="#f97316" />
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: isDark ? '#e2e8f0' : '#0f172a' }}>
                No Element Selected
              </Typography>
              <Typography sx={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b', px: 2 }}>
                Click on any element on the canvas to inspect and edit its live properties.
              </Typography>
            </Box>
          ) : (
            <>
              {/* + Add New Element Shortcut */}
              {onAddWidget && (
                <Button
                  variant="outlined"
                  size="small"
                  fullWidth
                  startIcon={<Plus size={13} />}
                  onClick={() => onAddWidget(selectedWidget.type)}
                  sx={{
                    height: 28,
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'none',
                    borderColor: 'rgba(249, 115, 22, 0.35)',
                    bgcolor: 'rgba(249, 115, 22, 0.08)',
                    color: '#f97316',
                    borderRadius: 1.5,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: '#f97316',
                      bgcolor: 'rgba(249, 115, 22, 0.18)',
                      color: '#ffffff'
                    }
                  }}
                >
                  + Add New {selectedWidget.type.charAt(0).toUpperCase() + selectedWidget.type.slice(1)}
                </Button>
              )}

              {/* ==================== CHART SETTINGS ==================== */}
              {['chart', 'bar', 'horizontal_bar', 'line', 'area', 'pie', 'donut', 'gauge', 'metric', 'table', 'heatmap'].includes(selectedWidget.type) && (() => {
                const currentChartType = selectedWidget.properties.chart_type || selectedWidget.properties.chartType || selectedWidget.properties.widget_type || 'bar';
                const isAxisChart = !['metric', 'gauge', 'pie', 'donut', 'table'].includes(currentChartType);
                const isGauge = currentChartType === 'gauge';
                const isMetric = currentChartType === 'metric';

                return (
                  <>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Title & Header
                      </Typography>
                      <TextField
                        label="Chart Title"
                        value={selectedWidget.properties.chartTitle || selectedWidget.properties.title || selectedWidget.properties.name || ''}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, {
                          chartTitle: e.target.value,
                          title: e.target.value,
                          name: e.target.value
                        })}
                        fullWidth
                        size="small"
                      />
                    </Box>

                    {/* Chart Query & Data Source Summary Card */}
                    <Box sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      bgcolor: isDark ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                      border: isDark ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#f97316', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Data Source & Query
                        </Typography>
                        <Typography sx={{ fontSize: 9.5, fontWeight: 600, color: '#38bdf8', bgcolor: isDark ? 'rgba(56, 189, 248, 0.1)' : 'rgba(56, 189, 248, 0.15)', px: 0.75, py: 0.1, borderRadius: 1 }}>
                          {String(currentChartType).toUpperCase()}
                        </Typography>
                      </Box>

                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, fontSize: 10.5 }}>
                        <Box>
                          <Typography sx={{ fontSize: 9.5, color: isDark ? '#64748b' : '#94a3b8' }}>Source</Typography>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {selectedWidget.properties.query_definition?.source || selectedWidget.properties.dataSource || selectedWidget.properties.dataset_name || 'N/A'}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 9.5, color: isDark ? '#64748b' : '#94a3b8' }}>Metric</Typography>
                          <Typography sx={{ fontSize: 11, fontWeight: 600, color: isDark ? '#f1f5f9' : '#0f172a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {selectedWidget.properties.query_definition?.metrics?.[0]?.field || selectedWidget.properties.metric_field || selectedWidget.properties.yAxisField || 'Count'} ({selectedWidget.properties.query_definition?.metrics?.[0]?.type || selectedWidget.properties.aggregation || 'count'})
                          </Typography>
                        </Box>
                      </Box>

                      {onOpenChartModal && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={onOpenChartModal}
                          sx={{
                            fontSize: 10.5,
                            textTransform: 'none',
                            height: 24,
                            borderColor: '#38bdf8',
                            color: '#38bdf8',
                            '&:hover': { bgcolor: 'rgba(56, 189, 248, 0.1)', borderColor: '#38bdf8' }
                          }}
                        >
                          Change Query / Chart Type
                        </Button>
                      )}
                    </Box>

                    {/* Color Palette Selector */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Color Palette Theme
                      </Typography>
                      <FormControl fullWidth size="small">
                        <InputLabel>Theme</InputLabel>
                        <Select
                          value={selectedWidget.properties.colorPalette || 'classic'}
                          label="Theme"
                          onChange={(e) => {
                            const pal = COLOR_PALETTES.find(p => p.value === e.target.value);
                            onUpdateWidget(selectedWidget.id, {
                              colorPalette: e.target.value,
                              chartColors: pal ? pal.colors : undefined,
                              accentColor: pal ? pal.colors[0] : undefined
                            });
                          }}
                        >
                          {COLOR_PALETTES.map((palette) => (
                            <MenuItem key={palette.value} value={palette.value}>
                              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                <Typography sx={{ fontSize: 11.5 }}>{palette.label}</Typography>
                                <Box sx={{ display: 'flex', gap: 0.35 }}>
                                  {palette.colors.slice(0, 5).map((c, i) => (
                                    <Box key={i} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
                                  ))}
                                </Box>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Primary Accent Color for Chart */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Primary Accent</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {PRESET_COLORS.map((c) => {
                            const isSelectedAccent = (selectedWidget.properties.accentColor === c.value) || (!selectedWidget.properties.accentColor && c.value === '#3b82f6');
                            return (
                              <Box
                                key={c.value}
                                onClick={() => {
                                  const pal = COLOR_PALETTES.find(p => p.value === selectedWidget.properties.colorPalette) || COLOR_PALETTES[0];
                                  const updatedColors = [c.value, ...pal.colors.filter(col => col !== c.value)];
                                  onUpdateWidget(selectedWidget.id, {
                                    accentColor: c.value,
                                    chartColors: updatedColors
                                  });
                                }}
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  bgcolor: c.value,
                                  cursor: 'pointer',
                                  border: isSelectedAccent ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.2)'
                                }}
                                title={c.label}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                    </Box>

                    {/* Gauge Settings */}
                    {isGauge && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Gauge Limits
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                          <TextField
                            label="Min Value"
                            type="number"
                            size="small"
                            value={selectedWidget.properties.gaugeMin ?? 0}
                            onChange={(e) => onUpdateWidget(selectedWidget.id, { gaugeMin: Number(e.target.value) })}
                          />
                          <TextField
                            label="Max Value"
                            type="number"
                            size="small"
                            value={selectedWidget.properties.gaugeMax ?? 100}
                            onChange={(e) => onUpdateWidget(selectedWidget.id, { gaugeMax: Number(e.target.value) })}
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Metric Card Settings */}
                    {isMetric && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Metric Format
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                          <TextField
                            label="Prefix (e.g. $)"
                            size="small"
                            value={selectedWidget.properties.metricPrefix || ''}
                            onChange={(e) => onUpdateWidget(selectedWidget.id, { metricPrefix: e.target.value })}
                          />
                          <TextField
                            label="Suffix (e.g. %)"
                            size="small"
                            value={selectedWidget.properties.metricSuffix || ''}
                            onChange={(e) => onUpdateWidget(selectedWidget.id, { metricSuffix: e.target.value })}
                          />
                        </Box>
                      </Box>
                    )}

                    {/* Toggles */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        Display Options
                      </Typography>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={selectedWidget.properties.showLegend !== false}
                            onChange={(e) => onUpdateWidget(selectedWidget.id, { showLegend: e.target.checked })}
                          />
                        }
                        label={<Typography sx={{ fontSize: 11.5 }}>Show Legend</Typography>}
                      />
                      {isAxisChart && (
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={selectedWidget.properties.showGrid !== false}
                              onChange={(e) => onUpdateWidget(selectedWidget.id, { showGrid: e.target.checked })}
                            />
                          }
                          label={<Typography sx={{ fontSize: 11.5 }}>Show Grid Lines</Typography>}
                        />
                      )}
                      {currentChartType === 'table' && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mt: 0.5 }}>
                          <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            Table Appearance & Density
                          </Typography>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={selectedWidget.properties.fitToPage !== false}
                                onChange={(e) => onUpdateWidget(selectedWidget.id, { fitToPage: e.target.checked })}
                              />
                            }
                            label={<Typography sx={{ fontSize: 11.5, fontWeight: 600 }}>Fit All Columns to Page</Typography>}
                          />
                          <FormControl size="small" fullWidth>
                            <InputLabel id="table-density-label">Density</InputLabel>
                            <Select
                              labelId="table-density-label"
                              label="Density"
                              value={selectedWidget.properties.density || 'auto'}
                              onChange={(e) => onUpdateWidget(selectedWidget.id, { density: e.target.value })}
                            >
                              <MenuItem value="auto">Auto (Adaptive)</MenuItem>
                              <MenuItem value="compact">Compact (Dense)</MenuItem>
                              <MenuItem value="comfortable">Comfortable</MenuItem>
                            </Select>
                          </FormControl>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={selectedWidget.properties.wrapHeaders !== false}
                                onChange={(e) => onUpdateWidget(selectedWidget.id, { wrapHeaders: e.target.checked })}
                              />
                            }
                            label={<Typography sx={{ fontSize: 11.5 }}>Wrap Column Headers</Typography>}
                          />
                        </Box>
                      )}
                    </Box>
                  </>
                );
              })()}

              {/* ==================== HEADING SETTINGS ==================== */}
              {selectedWidget.type === 'heading' && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Heading Content
                    </Typography>
                    <TextField
                      label="Title Text"
                      value={selectedWidget.properties.content || ''}
                      onChange={(e) => onUpdateWidget(selectedWidget.id, { content: e.target.value })}
                      fullWidth
                      size="small"
                      multiline
                      minRows={1}
                      maxRows={3}
                    />
                  </Box>

                  {/* Level / Variant Presets */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Heading Level
                    </Typography>
                    <ToggleButtonGroup
                      value={selectedWidget.properties.headingLevel || 'h1'}
                      exclusive
                      size="small"
                      fullWidth
                      onChange={(_, val) => {
                        if (!val) return;
                        const sizeMap: Record<string, number> = { h1: 28, h2: 22, h3: 18 };
                        onUpdateWidget(selectedWidget.id, {
                          headingLevel: val,
                          fontSize: sizeMap[val] || 24
                        });
                      }}
                    >
                      <ToggleButton value="h1">H1 (28px)</ToggleButton>
                      <ToggleButton value="h2">H2 (22px)</ToggleButton>
                      <ToggleButton value="h3">H3 (18px)</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  {/* Typography Controls */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Typography
                    </Typography>

                    <FormControl fullWidth size="small">
                      <InputLabel>Font Family</InputLabel>
                      <Select
                        value={selectedWidget.properties.fontFamily || 'Inter, sans-serif'}
                        label="Font Family"
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { fontFamily: e.target.value })}
                      >
                        {FONT_FAMILY_OPTIONS.map((f) => (
                          <MenuItem key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <TextField
                        label="Font Size (px)"
                        type="number"
                        size="small"
                        value={selectedWidget.properties.fontSize || 24}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { fontSize: Number(e.target.value) })}
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel>Weight</InputLabel>
                        <Select
                          value={selectedWidget.properties.fontWeight || 700}
                          label="Weight"
                          onChange={(e) => onUpdateWidget(selectedWidget.id, { fontWeight: Number(e.target.value) })}
                        >
                          <MenuItem value={400}>Regular (400)</MenuItem>
                          <MenuItem value={600}>Semi-Bold (600)</MenuItem>
                          <MenuItem value={700}>Bold (700)</MenuItem>
                          <MenuItem value={800}>Extra-Bold (800)</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Text Alignment */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Alignment</Typography>
                      <ToggleButtonGroup
                        value={selectedWidget.properties.textAlign || 'left'}
                        exclusive
                        size="small"
                        onChange={(_, val) => val && onUpdateWidget(selectedWidget.id, { textAlign: val })}
                      >
                        <ToggleButton value="left"><AlignLeft size={13} /></ToggleButton>
                        <ToggleButton value="center"><AlignCenter size={13} /></ToggleButton>
                        <ToggleButton value="right"><AlignRight size={13} /></ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Text Color */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Color</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {PRESET_COLORS.map((c) => (
                            <Box
                              key={c.value}
                              onClick={() => onUpdateWidget(selectedWidget.id, { color: c.value })}
                              sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: c.value,
                                cursor: 'pointer',
                                border: (selectedWidget.properties.color === c.value || (!selectedWidget.properties.color && c.value === '#0f172a')) ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.2)'
                              }}
                              title={c.label}
                            />
                          ))}
                        </Box>
                      </Box>
                      <TextField
                        type="color"
                        size="small"
                        value={selectedWidget.properties.color || '#0f172a'}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { color: e.target.value })}
                        fullWidth
                      />
                    </Box>
                  </Box>
                </>
              )}

              {/* ==================== BODY TEXT SETTINGS ==================== */}
              {selectedWidget.type === 'text' && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Content
                    </Typography>
                    <TextField
                      label="Body Text"
                      value={selectedWidget.properties.content || ''}
                      onChange={(e) => onUpdateWidget(selectedWidget.id, { content: e.target.value })}
                      fullWidth
                      multiline
                      minRows={3}
                      maxRows={6}
                      size="small"
                    />
                  </Box>

                  {/* Typography Controls */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Typography
                    </Typography>

                    <FormControl fullWidth size="small">
                      <InputLabel>Font Family</InputLabel>
                      <Select
                        value={selectedWidget.properties.fontFamily || 'Inter, sans-serif'}
                        label="Font Family"
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { fontFamily: e.target.value })}
                      >
                        {FONT_FAMILY_OPTIONS.map((f) => (
                          <MenuItem key={f.value} value={f.value} style={{ fontFamily: f.value }}>
                            {f.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <TextField
                        label="Font Size (px)"
                        type="number"
                        size="small"
                        value={selectedWidget.properties.fontSize || 13}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { fontSize: Number(e.target.value) })}
                      />
                      <TextField
                        label="Line Height"
                        type="number"
                        size="small"
                        inputProps={{ step: 0.1, min: 1, max: 3 }}
                        value={selectedWidget.properties.lineHeight || 1.5}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { lineHeight: Number(e.target.value) })}
                      />
                    </Box>

                    {/* Align & Style */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Alignment</Typography>
                      <ToggleButtonGroup
                        value={selectedWidget.properties.textAlign || 'left'}
                        exclusive
                        size="small"
                        onChange={(_, val) => val && onUpdateWidget(selectedWidget.id, { textAlign: val })}
                      >
                        <ToggleButton value="left"><AlignLeft size={13} /></ToggleButton>
                        <ToggleButton value="center"><AlignCenter size={13} /></ToggleButton>
                        <ToggleButton value="right"><AlignRight size={13} /></ToggleButton>
                      </ToggleButtonGroup>
                    </Box>

                    {/* Text Color */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 11, color: '#94a3b8' }}>Color</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {PRESET_COLORS.map((c) => (
                            <Box
                              key={c.value}
                              onClick={() => onUpdateWidget(selectedWidget.id, { color: c.value })}
                              sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                bgcolor: c.value,
                                cursor: 'pointer',
                                border: (selectedWidget.properties.color === c.value || (!selectedWidget.properties.color && c.value === '#334155')) ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.2)'
                              }}
                              title={c.label}
                            />
                          ))}
                        </Box>
                      </Box>
                      <TextField
                        type="color"
                        size="small"
                        value={selectedWidget.properties.color || '#334155'}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { color: e.target.value })}
                        fullWidth
                      />
                    </Box>
                  </Box>
                </>
              )}

              {/* ==================== IMAGE SETTINGS ==================== */}
              {selectedWidget.type === 'image' && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Image File & Upload
                    </Typography>

                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleImageUpload(e, selectedWidget.id)}
                    />

                    <Button
                      variant="contained"
                      size="small"
                      fullWidth
                      disabled={isUploadingImage}
                      startIcon={isUploadingImage ? <CircularProgress size={14} color="inherit" /> : <Upload size={13} />}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{
                        height: 32,
                        fontSize: 11.5,
                        fontWeight: 600,
                        textTransform: 'none',
                        bgcolor: '#f97316',
                        '&:hover': { bgcolor: '#ea580c' }
                      }}
                    >
                      {isUploadingImage ? 'Uploading Image...' : (selectedWidget.properties.image_url || selectedWidget.properties.image_key ? 'Replace Image (Max 5MB)' : 'Upload Image (Max 5MB)')}
                    </Button>

                    {(selectedWidget.properties.original_name || selectedWidget.properties.image_name) && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: isDark ? 'rgba(255, 255, 255, 0.04)' : '#f1f5f9', px: 1, py: 0.5, borderRadius: 1 }}>
                        <Typography sx={{ fontSize: 10, color: isDark ? '#94a3b8' : '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '170px' }}>
                          {selectedWidget.properties.original_name || selectedWidget.properties.image_name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => onUpdateWidget(selectedWidget.id, { image_url: '', image_key: '', image_name: '', original_name: '' })}
                          sx={{ p: 0.2, color: '#ef4444' }}
                          title="Remove image"
                        >
                          <X size={12} />
                        </IconButton>
                      </Box>
                    )}
                  </Box>

                  {/* Fit & Aspect Ratio */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Sizing & Fitting
                    </Typography>

                    <FormControl fullWidth size="small">
                      <InputLabel>Object Fit</InputLabel>
                      <Select
                        value={selectedWidget.properties.imageFit || 'contain'}
                        label="Object Fit"
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { imageFit: e.target.value })}
                      >
                        <MenuItem value="contain">Contain (Keep Aspect Ratio)</MenuItem>
                        <MenuItem value="cover">Cover (Fill & Crop)</MenuItem>
                        <MenuItem value="fill">Fill (Stretch to Widget)</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      label="Border Radius (px)"
                      type="number"
                      size="small"
                      value={selectedWidget.properties.borderRadius || 0}
                      onChange={(e) => onUpdateWidget(selectedWidget.id, { borderRadius: Number(e.target.value) })}
                    />
                  </Box>
                </>
              )}

              {/* ==================== DIVIDER SETTINGS ==================== */}
              {selectedWidget.type === 'divider' && (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Divider Appearance
                    </Typography>

                    <FormControl fullWidth size="small">
                      <InputLabel>Line Style</InputLabel>
                      <Select
                        value={selectedWidget.properties.dividerStyle || selectedWidget.properties.borderStyle || 'solid'}
                        label="Line Style"
                        onChange={(e) => onUpdateWidget(selectedWidget.id, {
                          borderStyle: e.target.value,
                          dividerStyle: e.target.value as 'solid' | 'dashed' | 'dotted' | 'none'
                        })}
                      >
                        <MenuItem value="solid">Solid Line</MenuItem>
                        <MenuItem value="dashed">Dashed Line</MenuItem>
                        <MenuItem value="dotted">Dotted Line</MenuItem>
                      </Select>
                    </FormControl>

                    <TextField
                      label="Thickness (px)"
                      type="number"
                      size="small"
                      value={selectedWidget.properties.borderWidth || 1}
                      onChange={(e) => onUpdateWidget(selectedWidget.id, { borderWidth: Number(e.target.value) })}
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 11, color: isDark ? '#94a3b8' : '#64748b' }}>Line Color</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {PRESET_COLORS.map((c) => {
                            const currentColor = selectedWidget.properties.color || selectedWidget.properties.borderColor || '#e2e8f0';
                            return (
                              <Box
                                key={c.value}
                                onClick={() => onUpdateWidget(selectedWidget.id, {
                                  color: c.value,
                                  borderColor: c.value
                                })}
                                sx={{
                                  width: 14,
                                  height: 14,
                                  borderRadius: '50%',
                                  bgcolor: c.value,
                                  cursor: 'pointer',
                                  border: currentColor.toLowerCase() === c.value.toLowerCase() ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.2)'
                                }}
                                title={c.label}
                              />
                            );
                          })}
                        </Box>
                      </Box>
                      <TextField
                        type="color"
                        size="small"
                        value={selectedWidget.properties.color || selectedWidget.properties.borderColor || '#e2e8f0'}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, {
                          color: e.target.value,
                          borderColor: e.target.value
                        })}
                        fullWidth
                      />
                    </Box>
                  </Box>
                </>
              )}

              {/* ==================== BORDER & FRAME STYLING (For Text and Heading Widgets) ==================== */}
              {!['chart', 'bar', 'horizontal_bar', 'line', 'area', 'pie', 'donut', 'gauge', 'metric', 'table', 'heatmap', 'image', 'divider'].includes(selectedWidget.type) && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <Typography sx={{ color: '#f97316', fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Border & Frame Styling
                  </Typography>

                  {/* Quick Border Presets */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography sx={{ fontSize: 10, color: isDark ? '#94a3b8' : '#64748b' }}>Quick Border Presets</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75 }}>
                      {[
                        { label: 'None', updates: { borderWidth: 0, borderLeftWidth: 0, borderBottomWidth: 0, backgroundColor: 'transparent' } },
                        { label: '1px Outline', updates: { borderWidth: 1, borderColor: '#cbd5e1', borderStyle: 'solid', borderLeftWidth: 0, borderBottomWidth: 0, borderRadius: 4 } },
                        { label: 'Left Accent', updates: { borderWidth: 0, borderLeftWidth: 4, borderLeftColor: '#f97316', borderLeftStyle: 'solid', borderBottomWidth: 0, borderRadius: 0 } },
                        { label: 'Accent + Box', updates: { borderWidth: 1, borderColor: '#e2e8f0', borderStyle: 'solid', borderLeftWidth: 4, borderLeftColor: '#3b82f6', borderLeftStyle: 'solid', borderRadius: 4 } },
                        { label: 'Underline', updates: { borderWidth: 0, borderLeftWidth: 0, borderBottomWidth: 2, borderBottomColor: '#f97316', borderBottomStyle: 'solid', borderRadius: 0 } },
                        { label: 'Dashed Box', updates: { borderWidth: 1, borderColor: '#94a3b8', borderStyle: 'dashed', borderLeftWidth: 0, borderBottomWidth: 0, borderRadius: 4 } }
                      ].map((preset) => (
                        <Button
                          key={preset.label}
                          variant="outlined"
                          size="small"
                          onClick={() => onUpdateWidget(selectedWidget.id, preset.updates)}
                          sx={{
                            fontSize: 10.5,
                            fontWeight: 600,
                            textTransform: 'none',
                            py: 0.5,
                            px: 1,
                            borderColor: isDark ? '#334155' : '#cbd5e1',
                            color: isDark ? '#cbd5e1' : '#334155',
                            bgcolor: isDark ? '#1e293b' : '#f8fafc',
                            '&:hover': {
                              borderColor: '#f97316',
                              color: '#f97316',
                              bgcolor: isDark ? 'rgba(249, 115, 22, 0.1)' : 'rgba(249, 115, 22, 0.06)'
                            }
                          }}
                        >
                          {preset.label}
                        </Button>
                      ))}
                    </Box>
                  </Box>

                  {/* Full Border (Outline) */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', p: 1, borderRadius: 1.5, border: isDark ? '1px solid #1e293b' : '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>Full Border (Outline)</Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <TextField
                        label="Thickness (px)"
                        type="number"
                        size="small"
                        inputProps={{ min: 0, max: 12 }}
                        value={selectedWidget.properties.borderWidth ?? 0}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { borderWidth: Number(e.target.value) })}
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel>Style</InputLabel>
                        <Select
                          value={selectedWidget.properties.borderStyle || 'solid'}
                          label="Style"
                          onChange={(e) => onUpdateWidget(selectedWidget.id, { borderStyle: e.target.value })}
                        >
                          <MenuItem value="solid">Solid</MenuItem>
                          <MenuItem value="dashed">Dashed</MenuItem>
                          <MenuItem value="dotted">Dotted</MenuItem>
                          <MenuItem value="double">Double</MenuItem>
                          <MenuItem value="none">None</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Border Color */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 10, color: isDark ? '#94a3b8' : '#64748b' }}>Border Color</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {PRESET_COLORS.map((c) => (
                            <Box
                              key={c.value}
                              onClick={() => onUpdateWidget(selectedWidget.id, { borderColor: c.value })}
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: c.value,
                                cursor: 'pointer',
                                border: (selectedWidget.properties.borderColor === c.value) ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.2)'
                              }}
                              title={c.label}
                            />
                          ))}
                        </Box>
                      </Box>
                      <TextField
                        type="color"
                        size="small"
                        value={selectedWidget.properties.borderColor || '#cbd5e1'}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { borderColor: e.target.value })}
                        fullWidth
                      />
                    </Box>
                  </Box>

                  {/* Left Accent Bar (Callout Bar) */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, bgcolor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', p: 1, borderRadius: 1.5, border: isDark ? '1px solid #1e293b' : '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>Left Accent Bar (Callout)</Typography>
                    
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <TextField
                        label="Width (px)"
                        type="number"
                        size="small"
                        inputProps={{ min: 0, max: 16 }}
                        value={selectedWidget.properties.borderLeftWidth ?? 0}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { borderLeftWidth: Number(e.target.value) })}
                      />
                      <FormControl fullWidth size="small">
                        <InputLabel>Style</InputLabel>
                        <Select
                          value={selectedWidget.properties.borderLeftStyle || 'solid'}
                          label="Style"
                          onChange={(e) => onUpdateWidget(selectedWidget.id, { borderLeftStyle: e.target.value })}
                        >
                          <MenuItem value="solid">Solid</MenuItem>
                          <MenuItem value="dashed">Dashed</MenuItem>
                          <MenuItem value="dotted">Dotted</MenuItem>
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Left Accent Color */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 10, color: isDark ? '#94a3b8' : '#64748b' }}>Accent Color</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          {PRESET_COLORS.map((c) => (
                            <Box
                              key={c.value}
                              onClick={() => onUpdateWidget(selectedWidget.id, { borderLeftColor: c.value })}
                              sx={{
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                bgcolor: c.value,
                                cursor: 'pointer',
                                border: (selectedWidget.properties.borderLeftColor === c.value) ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.2)'
                              }}
                              title={c.label}
                            />
                          ))}
                        </Box>
                      </Box>
                      <TextField
                        type="color"
                        size="small"
                        value={selectedWidget.properties.borderLeftColor || '#f97316'}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { borderLeftColor: e.target.value })}
                        fullWidth
                      />
                    </Box>
                  </Box>

                  {/* Corner Radius, Padding & Background */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                    <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a' }}>Box Sizing & Background</Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                      <TextField
                        label="Corner Radius (px)"
                        type="number"
                        size="small"
                        inputProps={{ min: 0, max: 24 }}
                        value={selectedWidget.properties.borderRadius ?? 0}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { borderRadius: Number(e.target.value) })}
                      />
                      <TextField
                        label="Padding (px)"
                        type="number"
                        size="small"
                        inputProps={{ min: 0, max: 32 }}
                        value={selectedWidget.properties.padding ?? 4}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { padding: Number(e.target.value) })}
                      />
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: 10, color: isDark ? '#94a3b8' : '#64748b' }}>Background</Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => onUpdateWidget(selectedWidget.id, { backgroundColor: 'transparent' })}
                          sx={{ fontSize: 10, textTransform: 'none', p: 0, minWidth: 'auto', color: '#38bdf8' }}
                        >
                          Clear (Transparent)
                        </Button>
                      </Box>
                      <TextField
                        type="color"
                        size="small"
                        value={selectedWidget.properties.backgroundColor && selectedWidget.properties.backgroundColor !== 'transparent' ? selectedWidget.properties.backgroundColor : '#ffffff'}
                        onChange={(e) => onUpdateWidget(selectedWidget.id, { backgroundColor: e.target.value })}
                        fullWidth
                      />
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Delete Widget Action */}
              <Box sx={{ mt: 'auto', pt: 2, borderTop: isDark ? '1px solid #1f2937' : '1px solid #e2e8f0' }}>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  fullWidth
                  startIcon={<Trash2 size={13} />}
                  onClick={() => onDeleteWidget(selectedWidget.id)}
                  sx={{
                    height: 30,
                    fontSize: 11,
                    borderColor: '#ef4444',
                    color: '#ef4444',
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'rgba(239, 68, 68, 0.15)',
                      borderColor: '#dc2626',
                      color: '#f87171'
                    }
                  }}
                >
                  Delete Selected Element
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default RightPropsPanel;
